/**
 * StatusPill — a small dot + label pill for connection/import status.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type StatusPillTone = 'active' | 'paused' | 'neutral' | 'success' | 'warning' | 'danger';

export interface StatusPillProps {
  tone: StatusPillTone;
  children: ReactNode;
  className?: string;
}

const TONES: Record<StatusPillTone, { pill: string; dot: string }> = {
  active: {
    pill: 'border-success-border bg-success-bg text-success-foreground',
    dot: 'bg-success',
  },
  success: {
    pill: 'border-success-border bg-success-bg text-success-foreground',
    dot: 'bg-success',
  },
  paused: { pill: 'border-border-subtle bg-muted text-faint', dot: 'bg-[#b3b3b9]' },
  // Needs attention but nothing is broken — e.g. Brain is out of a group and
  // can be put back. `danger` would overstate it; `paused` would hide it.
  warning: {
    pill: 'border-warning-border bg-warning-bg text-warning',
    dot: 'bg-warning',
  },
  neutral: { pill: 'border-border-subtle bg-muted text-faint', dot: 'bg-[#b3b3b9]' },
  danger: {
    pill: 'border-destructive-border bg-destructive-bg text-destructive',
    dot: 'bg-destructive',
  },
};

export function StatusPill({ tone, children, className }: StatusPillProps) {
  const t = TONES[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        t.pill,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />
      {children}
    </span>
  );
}
